/**
 * thumbnailTrigger — pure "is a thumbnail export due?" rate-limiter.
 *
 * Deliberately tiny and side-effect free (mirrors `autosaveScheduler`'s
 * testing approach): the caller (the autosave success path) decides *when*
 * to ask, this module only decides *whether enough time has passed* for the
 * given file since the last generation. An injectable clock keeps it
 * deterministic under test — no hidden `Date.now()`/`setTimeout` calls here.
 */

export const DEFAULT_THUMBNAIL_MIN_INTERVAL_MS = 30000;

export interface CreateThumbnailTriggerOptions {
  /** Minimum time between thumbnail generations for the same file. */
  minIntervalMs?: number;
  /** Injectable clock for deterministic tests. Defaults to `Date.now`. */
  now?: () => number;
}

export interface ThumbnailTrigger {
  /** Whether enough time has elapsed since the last generation for `fileId`. */
  shouldGenerate: (fileId: string) => boolean;
  /** Record that a generation just happened for `fileId`. */
  recordGenerated: (fileId: string) => void;
  /**
   * Ms remaining before generation is allowed again for `fileId` (0 when it is
   * allowed right now, or was never generated). Lets the caller schedule a
   * trailing regeneration for exactly when the cooldown expires.
   */
  msUntilReady: (fileId: string) => number;
}

export const createThumbnailTrigger = (
  opts: CreateThumbnailTriggerOptions = {},
): ThumbnailTrigger => {
  const minIntervalMs = opts.minIntervalMs ?? DEFAULT_THUMBNAIL_MIN_INTERVAL_MS;
  const now = opts.now ?? Date.now;
  const lastGeneratedAt = new Map<string, number>();

  return {
    shouldGenerate: (fileId) => {
      const last = lastGeneratedAt.get(fileId);
      return last === undefined || now() - last >= minIntervalMs;
    },
    recordGenerated: (fileId) => {
      lastGeneratedAt.set(fileId, now());
    },
    msUntilReady: (fileId) => {
      const last = lastGeneratedAt.get(fileId);
      if (last === undefined) {
        return 0;
      }
      return Math.max(0, minIntervalMs - (now() - last));
    },
  };
};
